import React from 'react';
import ReportCard from '../components/ReportCard';
import GenderMarkerRows from '../components/GenderMarkerRows';
import { fmtDecimal, fmtPct, sentenceCaseMarker, share, spreadAtTwoPlus } from '../format';
import { Figure as Claim } from '../components/Emphasis';

/**
 * Where the gender gap actually is, marker by marker.
 *
 * Chapter 01's gap runs one way across every marker; here each marker skews to a different gender, so the
 * two summary cards name whichever markers each gender leads on rather than asserting a direction.
 */
const DeficiencyByGender = ({ data }) => {
  const rate = (marker, gender) => {
    const counts = (marker.by_gender || {})[gender] || {};
    return share(counts.affected, counts.tested);
  };

  const skew = (gender, other) =>
    data.markers
      .map((marker) => ({
        label: marker.label,
        mine: rate(marker, gender),
        theirs: rate(marker, other),
      }))
      .filter((entry) => entry.mine > entry.theirs)
      .sort((a, b) => b.mine - a.mine);

  const burden = (gender) => {
    const entry = data.by_gender.find((item) => item.gender === gender);
    return entry ? entry.avg_burden : 0;
  };

  const cards = [
    { gender: 'Male', label: 'Men', leads: skew('Male', 'Female'), avg: burden('Male') },
    { gender: 'Female', label: 'Women', leads: skew('Female', 'Male'), avg: burden('Female') },
  ];

  const other = (card) => cards.find((entry) => entry.gender !== card.gender) || card;

  // The design calls gender "the real fault line". Only claim that when the gender gap at 2+ is
  // genuinely wider than the spread across age bands.
  const genderSharper = spreadAtTwoPlus(data.by_gender) > spreadAtTwoPlus(data.by_age);

  return (
    <div className='mt-ds-9'>
      <h3 className='m-0 mb-[6px] font-fig-extrabold text-[28px] font-bold leading-[1.18] text-gray-900'>
        {genderSharper ? 'Gender is the real fault line' : 'The gender gap is narrower than the age spread'}
      </h3>
      <p className='m-0 mb-[22px] text-ts-md text-gray-600'>
        Each test skews sharply toward one gender — not a uniform pattern
      </p>

      <div className='grid grid-cols-1 items-start gap-ds-5 lg:grid-cols-[1.5fr_1fr]'>
        <ReportCard fileName='deficiency-by-gender' className='p-[26px]'>
          <GenderMarkerRows markers={data.markers} labelWidth={130} legendGap={6} />
        </ReportCard>

        <div className='grid gap-ds-4'>
          {cards.map((card) => {
            const lead = card.leads[0];
            const second = card.leads[1];
            const ratio = lead && lead.theirs > 0 ? lead.mine / lead.theirs : null;

            return (
              <div key={card.gender} className='rounded-ds-lg border border-gray-300 bg-white p-ds-6'>
                <div className='mb-ds-3 text-ts-xs font-semibold uppercase tracking-[0.12em] text-gray-600'>
                  {card.label}
                </div>
                {lead ? (
                  <p className='m-0 mb-[10px] text-ts-md text-gray-800'>
                    {ratio ? (
                      <>
                        <Claim>{`${ratio.toFixed(1)}x more likely`}</Claim>
                        {` to be flagged for ${sentenceCaseMarker(lead.label)}`}
                        {second ? ` and ${sentenceCaseMarker(second.label)}` : ''}
                        {` (${fmtPct(lead.mine)} vs ${fmtPct(lead.theirs)})`}
                      </>
                    ) : (
                      <>
                        {`Leads on ${sentenceCaseMarker(lead.label)} at `}
                        <Claim>{fmtPct(lead.mine)}</Claim>
                      </>
                    )}
                  </p>
                ) : (
                  <p className='m-0 mb-[10px] text-ts-md text-gray-800'>
                    No marker skews towards this group.
                  </p>
                )}
                {/* The design gives the two cards different closing lines: the heavier-burden group gets
                    its average, the other gets its runner-up marker. Both figures are still reported in
                    full by the "Reading the split" table below. */}
                {second && card.avg < other(card).avg ? (
                  <p className='m-0 text-[14px] text-gray-600'>
                    {`Also modestly higher ${sentenceCaseMarker(second.label)} abnormality`}
                  </p>
                ) : (
                  <p className='m-0 text-[14px] text-gray-600'>
                    Average{' '}
                    <span className='font-jetbrains-mono font-semibold text-gray-700'>
                      {fmtDecimal(card.avg)}
                    </span>
                    {' abnormalities per person'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DeficiencyByGender;
